// FE-SCRM-049 — React Query hooks for the Prospect surface.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useCallback } from "react";

import type { TargetSearchResult } from "../components";

import {
  archiveProspect,
  createProspect,
  fetchBrokerFirmSuggestions,
  fetchGlobalSearch,
  fetchProspects,
  fetchTagSuggestions,
  restoreProspect,
  type FetchProspectsQuery,
} from "./api";
import type {
  Prospect, ProspectCreateInput, ProspectListResponse,
} from "./types";


export const PROSPECTS_QUERY_KEY = "prospects" as const;


export function useProspectsQuery(
  query: FetchProspectsQuery,
): UseQueryResult<ProspectListResponse> {
  return useQuery({
    queryKey: [PROSPECTS_QUERY_KEY, "list", query],
    queryFn: ({ signal }) => fetchProspects(query, { signal }),
  });
}


export function useCreateProspectMutation(): UseMutationResult<
  Prospect, Error, ProspectCreateInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createProspect,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [PROSPECTS_QUERY_KEY] });
    },
  });
}


export function useArchiveProspectMutation(): UseMutationResult<
  Prospect, Error, string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: archiveProspect,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [PROSPECTS_QUERY_KEY] });
    },
  });
}


export function useRestoreProspectMutation(): UseMutationResult<
  Prospect, Error, string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: restoreProspect,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [PROSPECTS_QUERY_KEY] });
    },
  });
}


// Returns a stable suggestion-fetcher for the TagsInput / autocomplete
// components, satisfying the ``fetchSuggestions`` prop contract on
// FE-SCRM-060 components.

export function useBrokerFirmSuggestions(): (q: string) => Promise<string[]> {
  return useCallback(async (q: string) => {
    const { rows } = await fetchBrokerFirmSuggestions(q);
    return rows.map((r) => r.name);
  }, []);
}


export function useTagSuggestions(): (q: string) => Promise<
  { tag: string; count: number }[]
> {
  return useCallback(async (q: string) => {
    const { tags } = await fetchTagSuggestions(q, "prospect");
    return tags;
  }, []);
}


// Adapter that maps the global-search response to the
// TargetSearchPicker's ``TargetSearchResult`` shape. The picker
// surfaces only the entity kinds in ``restrictTo`` to keep the
// referral pickers scoped (account / sales_contact / carrier_contact).

export function useTargetSearchResults(): (
  q: string,
  restrictTo: readonly TargetSearchResult["kind"][] | undefined,
) => Promise<TargetSearchResult[]> {
  return useCallback(async (q, restrictTo) => {
    if (!q.trim()) return [];
    const data = await fetchGlobalSearch(q);
    const flat: TargetSearchResult[] = [];
    for (const row of data.prospects) {
      flat.push({ ...row, kind: "prospect" });
    }
    for (const row of data.opportunities) {
      flat.push({ ...row, kind: "opportunity" });
    }
    for (const row of data.sales_contacts) {
      flat.push({ ...row, kind: "contact" });
    }
    for (const row of data.carriers) {
      flat.push({ ...row, kind: "carrier" });
    }
    // Carrier contacts piggyback on the ``carrier`` kind for picker
    // purposes — TargetSearchPicker's enum doesn't distinguish the
    // two and the global-search response carries a ``carrier_*`` row
    // type prefix in ``sublabel`` already.
    for (const row of data.carrier_contacts) {
      flat.push({ ...row, kind: "carrier" });
    }
    if (!restrictTo || restrictTo.length === 0) return flat;
    return flat.filter((r) => restrictTo.includes(r.kind));
  }, []);
}
